import React, { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useSubscription } from "../../Context/SubscriptionContext";
import { useTimezone } from "../../Context/TimezoneContext";
import { useSnackbar } from "../../utils/useSnackbar";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import EventIcon from "@material-ui/icons/Event";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import ExpandMoreRoundedIcon from "@material-ui/icons/ExpandMoreRounded";
import mindtreeimg from "../../assets/mindtreetechnologies .png"
import * as api from "../../api";
import ConfirmationDialog from "../ConfirmationDialog/ConfirmationDialog";
import { beginLogout } from "../../utils/logoutGuard";
import TimezoneSelect from "./TimezoneSelect";

const useStyles = makeStyles((theme) => ({
  "@keyframes chipPulse": {
    "0%, 100%": { boxShadow: "0 0 0 0 rgba(220, 38, 38, 0.45)" },
    "50%": { boxShadow: "0 0 0 6px rgba(220, 38, 38, 0)" },
  },
  appBar: {
    background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
    boxShadow: "0 4px 20px rgba(234, 88, 12, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.25)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.18)",
    zIndex: theme.zIndex.drawer + 1,
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 24px",
    minHeight: 64,
    height: 64,
    [theme.breakpoints.down("xs")]: {
      padding: "0 12px",
      minHeight: 56,
      height: 56,
    },
  },
  logoSection: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
  },
  logoBadge: {
    background: "#ffffff",
    borderRadius: 10,
    padding: "4px 10px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 38,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.4)",
    flexShrink: 0,
    boxSizing: "border-box",
  },
  logo: {
    height: 26,
    width: "auto",
    maxWidth: 100,
    objectFit: "contain",
    display: "block",
  },
  titleDivider: {
    width: 1,
    height: 28,
    backgroundColor: "rgba(255, 255, 255, 0.35)",
    margin: "0 14px",
    flexShrink: 0,
  },
  titleWrap: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    [theme.breakpoints.down("xs")]: {
      gap: 7,
    },
  },
  iconBadge: {
    width: 34,
    height: 34,
    minWidth: 34,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255, 255, 255, 0.2)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    color: "#ffffff",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      width: 28,
      height: 28,
      minWidth: 28,
      borderRadius: 8,
    },
  },
  roleBadge: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "0.64rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "rgba(255, 255, 255, 0.95)",
    background: "rgba(255, 255, 255, 0.22)",
    border: "1px solid rgba(255, 255, 255, 0.25)",
    padding: "1px 7px",
    borderRadius: 4,
    lineHeight: 1.2,
    marginBottom: 2,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.55rem",
      padding: "0 5px",
      marginBottom: 1,
      letterSpacing: "0.05em",
    },
  },
  title: {
    fontWeight: 800,
    fontSize: "1.15rem",
    color: "#ffffff",
    letterSpacing: "-0.01em",
    lineHeight: 1.2,
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.15)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 320,
    [theme.breakpoints.down("sm")]: {
      maxWidth: 180,
      fontSize: "1rem",
    },
    [theme.breakpoints.down("xs")]: {
      maxWidth: 130,
      fontSize: "0.92rem",
    },
  },
  userSection: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      gap: 6,
    },
  },
  goToPosBtn: {
    height: 36,
    padding: "0 12px",
    borderRadius: 10,
    background: "rgba(255, 255, 255, 0.18)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    backdropFilter: "blur(6px)",
    color: "#ffffff",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.82rem",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    boxShadow: "none",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.28)",
      borderColor: "rgba(255, 255, 255, 0.45)",
      transform: "translateY(-1px)",
      boxShadow: "0 3px 8px rgba(0, 0, 0, 0.1)",
    },
    [theme.breakpoints.down("xs")]: {
      height: 34,
      padding: "0 8px",
      fontSize: "0.75rem",
      gap: 4,
    },
  },
  expiryPill: {
    height: 36,
    padding: "0 12px",
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    fontSize: "0.8rem",
    fontWeight: 700,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
    cursor: "default",
    backdropFilter: "blur(6px)",
    transition: "all 0.15s ease",
  },
  identityTrigger: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    height: 36,
    borderRadius: 18,
    padding: "2px 10px 2px 3px",
    background: "rgba(255, 255, 255, 0.18)",
    border: "1px solid rgba(255, 255, 255, 0.28)",
    backdropFilter: "blur(6px)",
    transition: "all 0.15s ease",
    boxSizing: "border-box",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.28)",
      borderColor: "rgba(255, 255, 255, 0.45)",
      transform: "translateY(-1px)",
    },
    [theme.breakpoints.down("xs")]: {
      height: 34,
      padding: "2px 6px 2px 2px",
      gap: 4,
    },
  },
  identityTriggerOpen: {
    background: "rgba(255, 255, 255, 0.28)",
    borderColor: "rgba(255, 255, 255, 0.45)",
  },
  avatar: {
    backgroundColor: "#ffffff",
    color: "#ea580c",
    fontWeight: 800,
    width: 30,
    height: 30,
    fontSize: "0.82rem",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.15)",
    [theme.breakpoints.down("xs")]: {
      width: 28,
      height: 28,
      fontSize: "0.75rem",
    },
  },
  userName: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "0.82rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 140,
    letterSpacing: "0.01em",
  },
  identityChevron: {
    color: "rgba(255, 255, 255, 0.9)",
    fontSize: "1.1rem",
    transition: "transform 0.15s ease",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1rem",
    },
  },
  identityChevronOpen: {
    transform: "rotate(180deg)",
  },
  menuPaper: {
    borderRadius: 14,
    minWidth: 230,
    border: "1px solid #e2e8f0",
    boxShadow: "0 14px 36px rgba(15, 23, 42, 0.16), 0 2px 8px rgba(15, 23, 42, 0.08)",
    marginTop: 8,
    overflow: "hidden",
  },
  menuHeader: {
    padding: "12px 16px 10px",
    borderBottom: "1px solid #f1f5f9",
    background: "#fafafa",
  },
  menuItem: {
    padding: "10px 14px",
    margin: "3px 6px",
    borderRadius: 8,
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  menuItemDanger: {
    padding: "10px 14px",
    margin: "3px 6px",
    borderRadius: 8,
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "#fef2f2",
    },
  },
  menuIconBadge: {
    width: 30,
    height: 30,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "@media (prefers-reduced-motion: reduce)": {
    chipUrgent: { animation: "none" },
    goToPosBtn: { transition: "none" },
    identityTrigger: { transition: "none" },
  },
}));

const SuperAdminNavBar = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  // Between mobile and desktop, the expiry chip plus avatar/name/logout can
  // crowd a mid-width window now that there's more in the bar - drop the
  // chip there first, it's the least essential piece at a glance.
  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [openSnackbar] = useSnackbar();
  const [user] = useState(
    JSON.parse(localStorage.getItem("profile"))
  );
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const { subscription, loading: expiryLoading } = useSubscription();
  const { timezone, setTimezone } = useTimezone();
  const [businessName, setBusinessName] = useState(null);
  const [businessNameLoading, setBusinessNameLoading] = useState(true);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);

  // Sets the flag App.js's top-level branch reads to decide SuperAdmin shell
  // vs regular POS UI, then hard-navigates - a hard reload (not
  // history.push) because that branch choice is only ever evaluated once
  // per page load, not reactively. Header.js's "Go to SuperAdmin Panel"
  // (shown there only to a superAdmin, since reaching that regular UI at
  // all already means this flag is set) is the way back.
  const handleGoToPos = async () => {
    try {
      await api.recordProgramLogEvent({ event: "SWITCH_TO_POS" });
    } catch (error) {
      // An audit failure must not trap a SuperAdmin in this view.
      console.warn("Could not record the POS view switch", error);
    }
    localStorage.setItem("superAdminViewingPos", "true");
    window.location.href = "/PosMainScreen";
  };

  const handleTimezoneChange = (nextTimezone) => {
    if (!nextTimezone || nextTimezone === timezone) return;
    setTimezone(nextTimezone);
    api
      .recordProgramLogEvent({
        event: "DISPLAY_TIMEZONE_CHANGED",
        previousTimezone: timezone,
        timezone: nextTimezone,
      })
      .catch((error) => console.warn("Could not record the timezone change", error));
  };

  // Same public, unauthenticated endpoint Login.js uses - lets the
  // superAdmin see which deployment/company they're managing at a glance,
  // since this app is deployed per-client (different business name/logo
  // each time).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.fetchPublicBusinessName();
        if (data?.businessName) setBusinessName(data.businessName);
      } catch (error) {
        // Non-fatal - subtitle just falls back to the generic text.
      } finally {
        setBusinessNameLoading(false);
      }
    })();
  }, []);

  // Always visible while managing the deployment, not just at login - the
  // superAdmin is exactly the person who'd act on this. Derived from the
  // shared SubscriptionContext (was: its own independent fetch) so extending
  // the subscription in SubscriptionTab.js updates this chip immediately
  // instead of only after a full page reload.
  // Solid/near-solid backgrounds for the urgent tiers (was: translucent white
  // overlays, which - on top of an orange bar - blended straight into it and
  // read as barely-there). Only the calm "plenty of time left" tier stays
  // translucent; it doesn't need to shout.
  const expiryChip = useMemo(() => {
    if (!subscription?.subscriptionExpiry) {
      return {
        label: "No expiry set",
        color: "#ffffff",
        bg: "rgba(255, 255, 255, 0.18)",
        detail: "No deployment expiry date is set.",
        icon: EventIcon,
        borderColor: "rgba(255, 255, 255, 0.28)",
        urgent: false,
      };
    }
    const expiryDate = new Date(subscription.subscriptionExpiry);
    const dateLabel = expiryDate.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    const daysLeft = Math.ceil((expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) {
      return {
        label: `Expired ${Math.abs(daysLeft)}d ago`,
        color: "#dc2626",
        bg: "#ffffff",
        borderColor: "#fca5a5",
        urgent: true,
        icon: ReportProblemOutlinedIcon,
        detail: `Expired on ${dateLabel}. Extend it from the Subscription tab.`,
      };
    }
    if (daysLeft <= 10) {
      return {
        label: `Expires in ${daysLeft}d`,
        color: "#c2410c",
        bg: "#fffaf5",
        borderColor: "#fdba74",
        urgent: true,
        icon: ReportProblemOutlinedIcon,
        detail: `Expires ${dateLabel}. Extend it from the Subscription tab before it lapses.`,
      };
    }
    return {
      label: `Active • ${daysLeft}d left`,
      color: "#ffffff",
      bg: "rgba(255, 255, 255, 0.18)",
      borderColor: "rgba(255, 255, 255, 0.28)",
      urgent: false,
      icon: EventIcon,
      detail: `Expires ${dateLabel}.`,
    };
  }, [subscription]);

  const handleLogout = () => {
    beginLogout();
    setLogoutConfirmOpen(false);
    openSnackbar("Logged out successfully");
    dispatch({ type: "LOGOUT" });
  };

  const getInitials = (name) => {
    if (!name) return "SA";
    const names = name.split(" ");
    return names
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  if (!user) return null;

  return (
    <AppBar position="fixed" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        <Box className={classes.logoSection}>
          {!isMobile && (
            <span className={classes.logoBadge}>
              <img
                src={mindtreeimg}
                alt="MindTree"
                className={classes.logo}
              />
            </span>
          )}

          {!isMobile && <div className={classes.titleDivider} />}

          <div className={classes.titleWrap}>
            <span className={classes.iconBadge}>
              <SupervisorAccountIcon fontSize="small" />
            </span>
            <div style={{ minWidth: 0 }}>
              <Typography component="div" className={classes.roleBadge}>
                Super Admin
              </Typography>
              {businessNameLoading ? (
                <Skeleton
                  variant="text"
                  width={isMobile ? 90 : 140}
                  height={isMobile ? 18 : 22}
                  style={{ backgroundColor: "rgba(255,255,255,0.25)" }}
                />
              ) : (
                <Typography variant="h6" className={classes.title}>
                  {businessName || (isMobile ? "Admin Panel" : "Subscription · Users · Global Settings")}
                </Typography>
              )}
            </div>
          </div>
        </Box>

        <Box className={classes.userSection}>
          <TimezoneSelect value={timezone} onChange={handleTimezoneChange} />

          {!isCompact &&
            (expiryLoading ? (
              <Skeleton
                variant="rect"
                width={110}
                height={36}
                style={{ borderRadius: 10, backgroundColor: "rgba(255,255,255,0.2)" }}
              />
            ) : (
              expiryChip && (
                <Tooltip title={expiryChip.detail} arrow placement="bottom">
                  <div
                    className={`${classes.expiryPill} ${expiryChip.urgent ? classes.chipUrgent : ""}`}
                    style={{
                      backgroundColor: expiryChip.bg,
                      color: expiryChip.color,
                      border: `1.5px solid ${expiryChip.borderColor}`,
                    }}
                  >
                    <expiryChip.icon style={{ color: expiryChip.color, fontSize: 18 }} />
                    <span>{expiryChip.label}</span>
                  </div>
                </Tooltip>
              )
            ))}

          <Tooltip title="Switch to the store's regular POS UI" arrow placement="bottom">
            <Button
              variant="text"
              className={classes.goToPosBtn}
              onClick={handleGoToPos}
              startIcon={<StorefrontOutlinedIcon style={{ fontSize: isMobile ? 16 : 18, color: "#ffffff" }} />}
            >
              {isMobile ? "POS" : "Go to POS"}
            </Button>
          </Tooltip>

          <Tooltip title="Account menu" arrow placement="bottom">
            <div
              className={`${classes.identityTrigger} ${Boolean(profileMenuAnchor) ? classes.identityTriggerOpen : ""}`}
              role="button"
              tabIndex={0}
              onClick={(e) => setProfileMenuAnchor(e.currentTarget)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setProfileMenuAnchor(e.currentTarget)}
              aria-haspopup="true"
              aria-expanded={Boolean(profileMenuAnchor)}
            >
              <Avatar className={classes.avatar}>
                {getInitials(user?.result?.name)}
              </Avatar>
              {!isMobile && (
                <Typography className={classes.userName}>
                  {user?.result?.name || "Super Admin"}
                </Typography>
              )}
              <ExpandMoreRoundedIcon
                className={`${classes.identityChevron} ${Boolean(profileMenuAnchor) ? classes.identityChevronOpen : ""}`}
              />
            </div>
          </Tooltip>

          <Menu
            anchorEl={profileMenuAnchor}
            open={Boolean(profileMenuAnchor)}
            onClose={() => setProfileMenuAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            classes={{ paper: classes.menuPaper }}
            style={{ zIndex: 10010 }}
          >
            <div className={classes.menuHeader}>
              <Typography style={{ fontSize: "0.875rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>
                {user?.result?.name || "Super Admin"}
              </Typography>
              <Typography style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 2 }}>
                {user?.result?.email || "Super Administrator"}
              </Typography>
            </div>
            <MenuItem
              className={classes.menuItem}
              onClick={() => {
                setProfileMenuAnchor(null);
                handleGoToPos();
              }}
            >
              <ListItemIcon style={{ minWidth: 38 }}>
                <div className={classes.menuIconBadge} style={{ background: "#fff7ed", color: "#ea580c" }}>
                  <StorefrontOutlinedIcon fontSize="small" />
                </div>
              </ListItemIcon>
              <ListItemText
                primary="Go to POS"
                secondary="Switch to store's regular POS UI"
                primaryTypographyProps={{ style: { fontSize: "0.84rem", fontWeight: 600, color: "#1e293b" } }}
                secondaryTypographyProps={{ style: { fontSize: "0.72rem", color: "#64748b" } }}
              />
            </MenuItem>
            <Divider style={{ margin: "4px 0" }} />
            <MenuItem
              className={classes.menuItemDanger}
              onClick={() => {
                setProfileMenuAnchor(null);
                setLogoutConfirmOpen(true);
              }}
            >
              <ListItemIcon style={{ minWidth: 38 }}>
                <div className={classes.menuIconBadge} style={{ background: "#fef2f2", color: "#dc2626" }}>
                  <ExitToAppIcon fontSize="small" />
                </div>
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ style: { fontSize: "0.84rem", fontWeight: 600, color: "#dc2626" } }}
              />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      <ConfirmationDialog
        open={logoutConfirmOpen}
        title="Log out?"
        message="You'll need to sign in again to access the SuperAdmin panel."
        confirmLabel="Log out"
        onCancel={() => setLogoutConfirmOpen(false)}
        onConfirm={handleLogout}
      />
    </AppBar>
  );
};

export default SuperAdminNavBar;

